<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <h6 class="element-header">Coach-Terminauswertung</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">Soziotherapie</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :coachDropdown="coachDropdown"
            :statuDropdown='statusDropdown'
            :clientDropdown="clientDropdown"
            :statusDropdown="unemployedStatusFiltered"
            :operationTypeDropdown="socioTherapyOperationTypes"
            @search="getMeetingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getMeetingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <div class="btn-link" @click="sortBy('termin')">
                  <span>Termin</span>
                  <i :class="getIconClass('termin')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('status')">
                  <span>Status</span>
                  <i :class="getIconClass('status')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('location')">
                  <span>Standort</span>
                  <i :class="getIconClass('location')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('client')">
                  <span>Kostenträger</span>
                  <i :class="getIconClass('client')"></i>
                </div>
              </th>
              <th>VersicherungsNr.</th>
              <th>
                <div class="btn-link" @click="sortBy('unemployedFirstName')">
                  <span>Kunde</span>
                  <i :class="getIconClass('unemployedFirstName')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('coachFirstName')">
                  <span>Mitarbeiter</span>
                  <i :class="getIconClass('coachFirstName')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('operationType')">
                  <span>Maßnahmentyp</span>
                  <i :class="getIconClass('operationType')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('startTime')">
                  <span>Start</span>
                  <i :class="getIconClass('startTime')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('endTime')">
                  <span>Ende</span>
                  <i :class="getIconClass('endTime')"></i>
                </div>
              </th>
              <th class="text-right">
                <div class="btn-link" @click="sortBy('units')">
                  <span>Einheiten</span>
                  <i :class="getIconClass('units')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('signature')">
                  <span>Unterschrieben</span>
                  <i :class="getIconClass('signature')"></i>
                </div>
              </th>
              <th>
                <div class="btn-link" @click="sortBy('paid')">
                  <span>Bezahlt</span>
                  <i :class="getIconClass('paid')"></i>
                </div>
              </th>
              <th v-if="canViewBillingInfo">
                <div class="btn-link" @click="sortBy('invoiced')">
                  <span>Abgerechnet</span>
                  <i :class="getIconClass('invoiced')"></i>
                </div>
              </th>
              <th v-if="canViewBillingInfo">
                <div class="btn-link" @click="sortBy('billNumber')">
                  <span>Rechnungsnummer</span>
                  <i :class="getIconClass('billNumber')"></i>
                </div>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(data, index) in meetingsList.data" :key="index">
              <td>{{ data?.termin }}</td>
              <td>{{ $t(`coaching_status.${data?.status}`) }}</td>
              <td>{{ data?.location }}</td>
              <td>{{ data?.client }}</td>
              <td>{{ data?.insuranceNumber }}</td>
              <td>{{ data?.unemployedFirstName }} {{ data?.unemployedLastName }}</td>
              <td>{{ data?.coachFirstName }} {{ data?.coachLastName }}</td>
              <td>{{ $t(`socio_therapy_coaching_operation_type.${data?.operationType}`) }}</td>
              <td>{{ data?.startTime }}</td>
              <td>{{ data?.endTime }}</td>
              <td class="text-right">{{ data?.units }}</td>
              <td class="text-center">{{ data.signature ? 'Ja' : 'Nein' }}</td>
              <td class="text-center">{{ data.paid ? 'Ja' : 'Nein' }}</td>
              <td v-if="canViewBillingInfo" class="text-center">{{ data?.billed }}</td>
              <td v-if="canViewBillingInfo" class="text-center">{{ data?.invoiceNumber }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="meetingsList?.pagination?.total_pages > 1"
            :totalPages="meetingsList?.pagination?.total_pages"
            :currentPage="meetingsList?.pagination?.current_page"
            :totalItems="meetingsList?.pagination?.total_items"
            :perPage="meetingsList?.pagination?.items_per_page"
            @pagechanged="getMeetingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsCoachService from '@/api/Analytics/Coach';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import {
  getClientDropdown,
  getCoachDropdownForAnalytics,
  getOfficeByUserRoleDropdown
} from '@/shared/dropdowns';
import { downloadFile, getSortIconClass } from '@/shared/functions';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import Search from '@/views/App/Analytics/Coach/AppointmentEvaluation/SocioTherapyCoaching/Search.vue';
import { computed, onMounted, ref } from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const piniaEnumsState = piniaEnumsStore();

const piniaUserState = piniaUserStore();
const currentUserRole = piniaUserState.currentUser.user.roles[0];
const canViewBillingInfo = computed(() => ['ROLE_ADMIN', 'ROLE_ACCOUNTANT'].includes(currentUserRole));

const unemployedStatusFiltered       = computed(() => {
  return piniaEnumsState.enums.unemployedStatus.filter(status =>
      status.value === 'active' || status.value === 'finished' || status.value === 'new',
  );
});
const { socioTherapyOperationTypes } = computed(() => piniaEnumsState.enums).value;

const currentDate = new Date().getFullYear();

const filters = ref({
  coachId: '',
  'officeIds[]': [],
  clientId: '',
  status: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
});

const meetingsList   = ref([]);
const coachDropdown  = ref([]);
const officeDropdown = ref([]);
const clientDropdown = ref([]);
const statusDropdown = ref([]);
const isSearching    = ref(false);
const isLoaded       = ref(false);
const isExcelLoaded = ref(false);

async function getMeetingList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = meetingsList.value?.pagination?.items_per_page;

  try {
    const { data } = await analyticsCoachService.getSocioTherapyCoachingList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    meetingsList.value = data;
    isSearching.value  = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function getIconClass(key) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getMeetingList();
}

async function getExcelExport() {
  await downloadFile('/analytics/coach/appointment-evaluation/socio-therapy-coaching-excel-export',
      undefined,
      {
        ...filters.value,
        'officeIds[]': filters.value['officeIds[]'],
      },
      false,
      'GET',
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getMeetingList(),
    getOfficeByUserRoleDropdown(officeDropdown),
    getClientDropdown(clientDropdown),
    getCoachDropdownForAnalytics(coachDropdown),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>